import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export default function QuizLayout({
    children,
    onClose,
    showClose = true,
    theme = 'light',
    className = ''
}) {
    const [showAbortConfirm, setShowAbortConfirm] = React.useState(false);
    const bgClass = theme === 'dark'
        ? 'bg-[#0a0f18] text-white'
        : 'bg-[#f8fafc] text-slate-900';

    const layoutContent = (
        <div className={`fixed inset-0 z-[3000] w-full h-full flex flex-col items-center justify-center overflow-hidden font-sans ${bgClass}`}>
            {/* Fallback Styles for Quiz Animations */}
            <style dangerouslySetInnerHTML={{ __html: `
                .rotate-y-180 { transform: rotateY(180deg) !important; }
                .transform-style-3d { transform-style: preserve-3d !important; }
                .backface-hidden { backface-visibility: hidden !important; -webkit-backface-visibility: hidden !important; }
            `}} />

            {/* Ambient Background Glows */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full blur-[120px] opacity-20 ${theme === 'dark' ? 'bg-plantlix-500' : 'bg-plantlix-200'}`} />
                <div className={`absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full blur-[120px] opacity-10 ${theme === 'dark' ? 'bg-blue-500' : 'bg-blue-200'}`} />
            </div>

            {/* Max-width container for desktop charm - Fullscreen on Mobile */}
            <div className={`
                w-full h-full flex flex-col relative z-10
                md:max-w-5xl md:max-h-[95vh] md:rounded-[2rem] 
                md:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] 
                md:border md:border-white/10
                overflow-hidden
                ${theme === 'dark' ? 'bg-slate-900/90 md:bg-slate-900/60' : 'bg-white/80 md:bg-white/70'}
                backdrop-blur-xl
            `}>
                {/* Main content - Full area below header */}
                <div className={`flex-1 relative overflow-hidden min-h-0 ${className}`}>
                    {children}
                </div>

                {/* Global Close Button (Always present unless explicitly hidden) */}
                {onClose && showClose && (
                    <div className="absolute top-4 right-4 z-[4000] md:hidden">
                        <button 
                            onClick={() => setShowAbortConfirm(true)}
                            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white active:scale-90 transition-all shadow-lg"
                            aria-label="Quiz abbrechen"
                        >
                            <X className="w-5 h-5" strokeWidth={3} />
                        </button>
                    </div>
                )}

                {/* Glassmorphism Abort Confirmation Modal */}
                {showAbortConfirm && (
                    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                        <div className={`
                            w-full max-w-sm p-8 rounded-[2.5rem] border backdrop-blur-2xl shadow-2xl space-y-8 animate-in zoom-in-95 duration-300
                            ${theme === 'dark' ? 'bg-slate-900/80 border-white/10 text-white' : 'bg-white/90 border-slate-200 text-slate-900'}
                        `}>
                            <div className="text-center space-y-2">
                                <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-500/30">
                                    <X className="w-8 h-8 text-rose-500" strokeWidth={3} />
                                </div>
                                <h3 className="text-2xl font-black italic tracking-tight">Quiz abbrechen?</h3>
                                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white/60' : 'text-slate-500'}`}>
                                    Dein aktueller Fortschritt in dieser Runde geht verloren.
                                </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <button 
                                    onClick={onClose}
                                    className="w-full py-4 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black text-lg shadow-xl shadow-rose-500/20 transition-all active:scale-95"
                                >
                                    JA, ABBRECHEN
                                </button>
                                <button 
                                    onClick={() => setShowAbortConfirm(false)}
                                    className={`w-full py-4 rounded-2xl font-black text-lg transition-all active:scale-95 ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-slate-100 hover:bg-slate-200 border-slate-200'} border`}
                                >
                                    WEITERMACHEN
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(layoutContent, document.body);
}
